import NewLayout from "@components/new-ui/Layout";
import React from "react";
import { CasestudyCard, casestudyCards } from ".";
import { CaseStudyHerosection, CaseStudyInformation, FeatureList, Navbar, navLinks, OrganizationProfile } from "./alrannan-administrative-services";
import StatsInfo from "@components/new-ui/IndustriesSection/StatsInfo";

const IntercityXpress: React.FC = () => {
    return (
        <NewLayout
            title="InterCityXpress | WhatsApp for Logistics Success Story"
            description="Find out how InterCityXpress, a renowned logistics company from Saudi Arabia, doubled their business growth with Gallabox's WhatsApp automations."
            canonical="https://gallabox.com/case-study/intercityxpress"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/case-study/intercityxpress/intercityxpress-og.png" }}
        >
            <CaseStudyHerosection
                title="How InterCityXpress doubled their business on WhatsApp with Gallabox"
                imageUrl={"https://assets.gallabox.com/gb-home/case-study/intercityxpress/intercityxpress-hero.png"}
                redirectUrl={"/case-study"}
            />
            <StatsInfo
                statsInfo={[
                    { percentage: "90%", label: "Increase in business profits" },
                    { percentage: "80%", label: " Reduction in marketing/ad spend" },
                    { percentage: "90%", label: "Growth in customer base" },
                ]}
            />

            <section className="max-w-7xl mx-auto  md:grid md:grid-cols-6 mt-24 mb-24">
                <div className="bg-white col-span-1">
                    <div style={{ position: "sticky", top: "100px" }}>
                        <OrganizationProfile
                            companyProfileData={[{ label: "Category", value: ["Travel/Logistics"] }]}
                            product={{
                                title: "Features Used",
                                productLink: [
                                    { link: "https://gallabox.com/whatsapp-chatbot", label: "Chatbots" },
                                    { link: "https://gallabox.com/whatsapp-flows", label: "Flows" },
                                    { link: "https://gallabox.com/whatsapp-integrations", label: "Integrations" },
                                    { link: "https://gallabox.com/whatsapp-shared-inbox", label: "Shared team Inbox" },
                                ],
                            }}
                        />
                    </div>
                </div>
                <div className=" col-span-4 md:pl-8 px-4">
                    <div className="flex flex-col gap-y-12 ">
                        <section id="About">
                            <p>
                                Founded in 2020,
                                <a href="https://www.intercityxpress.com" className="text-blue-500 px-1">
                                    InterCityXpress
                                </a>
                                emerged to solve a critical gap in Saudi Arabia&apos;s booming logistics market. While traditional logistics providers chase efficiency at the
                                expense of customer experience, InterCityXpress built their business on a revolutionary promise: exact date and time delivery, 24/7 availability,
                                and genuine flexibility.
                            </p>
                            <p className="mt-5">
                                Operating across Riyadh, Jeddah, and Dammam, they&apos;ve become the premium logistics partner for businesses and individuals who refuse to
                                compromise on delivery excellence.
                            </p>
                        </section>

                        <section id="Challenges">
                            <CaseStudyInformation subTitle={"The blind spot in support that costs a lot"} className="mt-0" />

                            <p className="mt-2">
                                For a company obsessed with delivery precision, InterCityXpress faced an unprecedented challenge. As Arun Prashanth, IT Operations Manager,
                                explains: &quot;It was difficult to receive customer feedback and also map support queries to the right location.&quot; In the last-mile delivery
                                business, not knowing where your customers are or what they think is critical.
                            </p>
                            <p className="mt-5">
                                The manual workaround was painful. Their team had to get on a call with every incoming support query to understand context and solve problems.
                                Picture this: delivery captains making thousands of deliveries daily across three major cities, and the only way to confirm locations or gather
                                feedback was through individual phone calls. In an industry where success hinges on real-time information, this Stone Age approach was strangling
                                growth.
                            </p>
                        </section>

                        <section id="Solutions">
                            <CaseStudyInformation subTitle={"Gallabox was a peer-recommended choice"} className="mt-0" />
                            <p className="mt-2">
                                When it came time to find a solution, Gallabox became an obvious choice for InterCityXpress. &quot;We didn&apos;t do any evaluation with other
                                solutions because we had already known about Gallabox&apos;s capabilities,&quot; Arun shares. The decision came from trusted sources: &quot;I got
                                good reviews from my partners and other peers from the technical team.&quot;
                            </p>
                            <CaseStudyInformation subTitle={"Crossing 4 critical growth barriers on WhatsApp"} className="mt-12" />
                            <p className="mt-2">
                                InterCityXpress implemented Gallabox to tackle four critical objectives: generate leads, streamline customer communication, improve team
                                productivity, and automate customer support. The deployment focused on two powerful features:
                            </p>
                            <FeatureList
                                List={[
                                    { listItem: "Broadcast capabilities now push real-time delivery updates to customers, eliminating the guesswork around exact arrival times." },
                                    {
                                        listItem:
                                            "Chatbots handle everything from delivery confirmations to feedback collection, creating the digital feedback loop that was previously missing.",
                                    },
                                ]}
                                className="mt-4 bg-gray-100"
                            />
                        </section>

                        <section id="Impact">
                            <CaseStudyInformation subTitle={"The 100% impact story"} className="mt-0" />
                            <p className="mt-2">
                                When asked about Gallabox&apos;s impact on business growth, Arun doesn&apos;t hesitate: `&quot;100%.`&quot; This isn&apos;t hyperbole, it&apos;s
                                backed by transformational metrics across every aspect of the business:
                            </p>

                            <FeatureList
                                List={[
                                    { listItem: "Employee productivity surged 70%: Delivery captains focus on deliveries, not phone calls" },
                                    { listItem: "Customer satisfaction jumped 50%: Real-time updates mean no more anxious waiting" },
                                    { listItem: "Lead generation increased 50%: Automated follow-ups turn satisfied customers into brand advocates" },

                                    { listItem: "First-time conversion rates exploded 90%: Better communication builds immediate trust" },
                                    { listItem: "Customer base expanded 90%: Word-of-mouth flourishes when delivery promises are kept" },
                                    { listItem: "Marketing spend dropped 80%: Happy customers become the marketing department" },
                                    { listItem: "User base grew 70%: The mobile app adoption soared with integrated communication" },
                                    { listItem: "Gross profits leaped 90%: Efficiency gains flow straight to the bottom line" },
                                ]}
                                className="mt-5 bg-gray-100"
                            />

                            <CaseStudyInformation subTitle={"The compound effect of getting WhatsApp communication right"} className="mt-10" />

                            <p className="mt-2">
                                What makes InterCityXpress&apos;s success remarkable is how fixing the core problem of customer feedback and location tracking cascaded into
                                business transformation. When delivery captains know exactly where to go and customers can track their parcels in real-time, everything changes.
                            </p>

                            <p className="mt-5">
                                The 80% reduction in marketing spend tells a powerful story. In Saudi Arabia&apos;s competitive logistics market, InterCityXpress&apos; strategy
                                showed how proper execution beats expensive advertising all the time.
                            </p>
                        </section>
                    </div>
                </div>
                <div>
                    <div style={{ position: "sticky", top: "100px", minHeight: "510px" }} className="hidden md:block">
                        <Navbar links={navLinks} />
                    </div>
                </div>
            </section>
            <div className="max-w-6xl mx-auto">
                <section>
                    <h3 className="text-gray-900 mb-10 mt-5 text-left ml-4">Check out other customer stories</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto gap-10 mb-20">
                        {casestudyCards.slice(0, 3)?.map((data, index) => (
                            <CasestudyCard key={index} {...data} />
                        ))}
                    </div>
                </section>
            </div>
        </NewLayout>
    );
};

export default IntercityXpress;
